import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { motion } from 'framer-motion';

const Spinner = () => {
  return (
    <motion.div
      className="flex items-center justify-center py-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-5 h-5 border-2 border-gray-300 dark:border-gray-700 border-t-accent-primary rounded-full"
        style={{ borderTopColor: 'var(--accent)' }}
        animate={{ rotate: 360 }}
        transition={{
          duration: 0.8,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
    </motion.div>
  );
};

// Helper function to render text with clickable links
const renderTextWithLinks = (text) => {
  const urlRegex = /(https?:\/\/[^\s)]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    // Check if this part is a URL
    if (part.match(/^https?:\/\//)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80 transition-opacity"
          style={{ color: 'var(--accent)' }}
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const AgentNarrative = forwardRef(
  (
    {
      phases,
      stepInterval = 900,
      spinDuration = 600,
      autoStart = true,
      showHeader = true,
      onComplete,
    },
    ref
  ) => {
    const [state, setState] = useState(autoStart ? 'playing' : 'idle');
    const [visibleStepIndex, setVisibleStepIndex] = useState(-1); // -1 means nothing shown yet
    const [isSpinning, setIsSpinning] = useState(false);
    const timeoutsRef = useRef([]);
    const onCompleteRef = useRef(onComplete);
    const stepIntervalRef = useRef(stepInterval);
    const spinDurationRef = useRef(spinDuration);

    // Flatten all steps from all phases into a single array with phase context
    const allStepsRef = useRef([]);
    
    useEffect(() => {
      const steps = [];
      let globalStepNumber = 1;
      phases.forEach((phase) => {
        phase.steps.forEach((step, stepIdx) => {
          steps.push({
            ...step,
            phaseId: phase.id,
            phaseTitle: phase.title,
            phaseSubtitle: phase.subtitle,
            stepNumber: globalStepNumber++,
            isFirstInPhase: stepIdx === 0,
          });
        });
      });
      allStepsRef.current = steps;
    }, [phases]);

    const clearTimeouts = () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };

    useEffect(() => {
      onCompleteRef.current = onComplete;
      stepIntervalRef.current = stepInterval;
      spinDurationRef.current = spinDuration;
    }, [onComplete, stepInterval, spinDuration]);

    const reset = () => {
      clearTimeouts();
      setState('idle');
      setVisibleStepIndex(-1);
      setIsSpinning(false);
    };

    const start = () => {
      if (state === 'playing') return;
      reset();
      setState('playing');
    };

    useEffect(() => {
      if (state !== 'playing') return;

      const allSteps = allStepsRef.current;
      
      // Check if we're done
      if (visibleStepIndex >= allSteps.length) {
        setState('complete');
        setIsSpinning(false);
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
        return;
      }

      // Start: show first step immediately
      if (visibleStepIndex === -1) {
        setVisibleStepIndex(0);
        return;
      }

      // After showing a step, wait stepInterval then show spinner, then next step
      if (visibleStepIndex >= 0 && visibleStepIndex < allSteps.length) {
        const timeout = setTimeout(() => {
          setIsSpinning(true);
          // After spinner duration, show next step
          const spinnerTimeout = setTimeout(() => {
            setIsSpinning(false);
            setVisibleStepIndex((prev) => {
              const next = prev + 1;
              if (next >= allSteps.length) {
                setState('complete');
                if (onCompleteRef.current) {
                  onCompleteRef.current();
                }
              }
              return next;
            });
          }, spinDurationRef.current);
          timeoutsRef.current.push(spinnerTimeout);
        }, stepIntervalRef.current);
        timeoutsRef.current.push(timeout);
      }

      return () => {
        clearTimeouts();
      };
    }, [state, visibleStepIndex]);

    useEffect(() => {
      if (autoStart && state === 'idle') {
        start();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStart]);

    useEffect(() => {
      return () => {
        clearTimeouts();
      };
    }, []);

    useImperativeHandle(ref, () => ({
      start,
      reset,
    }));

    const allSteps = allStepsRef.current;
    const currentStep = visibleStepIndex >= 0 && visibleStepIndex < allSteps.length 
      ? allSteps[visibleStepIndex] 
      : null;
    const currentPhase = currentStep 
      ? phases.find((p) => p.id === currentStep.phaseId)
      : null;

    return (
      <div className="w-full max-w-[1200px] mx-auto">
        {/* Header */}
        {showHeader && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" style={{ color: 'var(--text)' }}>
                An agentic workflow that augments your team, putting a human in the loop where it matters
              </h2>
              {currentPhase?.subtitle && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" style={{ color: 'var(--text-sec)' }}>
                  {currentPhase.subtitle}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Panel */}
        <div
          className="relative overflow-hidden rounded-lg border transition-shadow duration-200 hover:shadow-md"
          style={{
            backgroundColor: 'var(--bg-elev-1)',
            borderColor: 'var(--keyline)',
            boxShadow: 'var(--shadow-0)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = 'var(--shadow-soft)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = 'var(--shadow-0)';
          }}
        >
          {/* Scan beam */}
          <div
            className="absolute inset-0 pointer-events-none opacity-60"
            style={{
              background: 'var(--g-iris)',
              filter: 'var(--blur-iris)',
            }}
          />

          {/* Content */}
          <div className="relative p-6 md:p-8">
            <div className="space-y-3 md:space-y-4">
              {/* Show all visible steps */}
              {allSteps.slice(0, visibleStepIndex + 1).map((step, idx) => {
                const stepPhase = phases.find((p) => p.id === step.phaseId);
                const isFirstInPhase = step.isFirstInPhase;
                const showPhaseHeader = isFirstInPhase && idx > 0;

                return (
                  <React.Fragment key={`${step.phaseId}-${step.id}-${idx}`}>
                    {/* Phase header - show before first step of each phase */}
                    {showPhaseHeader && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-6 pt-6 border-t first:mt-0 first:pt-0 first:border-0"
                        style={{ borderColor: 'var(--divider)' }}
                      >
                        <div className="mb-4">
                          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1" style={{ color: 'var(--text)' }}>
                            {stepPhase.title}
                          </h3>
                          {stepPhase.subtitle && (
                            <p className="text-sm text-gray-600 dark:text-gray-400" style={{ color: 'var(--text-sec)' }}>
                              {stepPhase.subtitle}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Phase header for very first step */}
                    {idx === 0 && isFirstInPhase && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mb-4"
                      >
                        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1" style={{ color: 'var(--text)' }}>
                          {stepPhase.title}
                        </h3>
                        {stepPhase.subtitle && (
                          <p className="text-sm text-gray-600 dark:text-gray-400" style={{ color: 'var(--text-sec)' }}>
                            {stepPhase.subtitle}
                          </p>
                        )}
                      </motion.div>
                    )}

                    {/* Step */}
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      className="grid grid-cols-[auto,1fr] gap-3 md:gap-4 items-start"
                      data-testid={`agent-step-${step.id}`}
                    >
                      {/* Step marker */}
                      <span
                        className="font-mono text-xs text-gray-500 dark:text-gray-500 mt-0.5"
                        style={{ color: 'var(--text-dim)' }}
                      >
                        {String(step.stepNumber).padStart(2, '0')}
                      </span>

                      {/* Step text */}
                      <p
                        className="text-sm md:text-base leading-relaxed text-gray-600 dark:text-gray-400"
                        style={{ color: 'var(--text-sec)' }}
                      >
                        {renderTextWithLinks(step.text)}
                      </p>
                    </motion.div>
                  </React.Fragment>
                );
              })}

              {/* Spinner after the last visible step */}
              {visibleStepIndex >= 0 && visibleStepIndex < allSteps.length - 1 && isSpinning && (
                <Spinner />
              )}
            </div>
          </div>
        </div>

        {/* Play button (if autoStart is false) */}
        {!autoStart && state === 'idle' && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={start}
              className="px-6 py-3 bg-accent-primary text-white rounded-lg font-medium transition-all duration-200 hover:shadow-md hover:shadow-accent-primary/30 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/60 focus-visible:ring-offset-2"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              Play workflow
            </button>
          </div>
        )}
      </div>
    );
  }
);

AgentNarrative.displayName = 'AgentNarrative';

export default AgentNarrative;
